import React from 'react'
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react'

interface SpacingInputProps {
	label: string
	values: {
		top?: string
		right?: string
		bottom?: string
		left?: string
	}
	onChange: (values: { top?: string; right?: string; bottom?: string; left?: string }) => void
	unit?: string
	placeholder?: string
}

export default function SpacingInput({
	label,
	values,
	onChange,
	unit = 'px',
	placeholder = '0'
}: SpacingInputProps) {
	const getNumericValueForDisplay = (value: string | undefined): string => {
		if (typeof value !== 'string') return ''
		
		// Se o valor termina com a unidade (ex: "10px"), remove-a
		if (value.endsWith(unit)) {
			const numericPart = value.replace(unit, '')
			return numericPart === '' ? '' : numericPart
		}
		
		// Se é um número puro (ex: "10" ou ""), retorna como está
		if (value.match(/^\d*$/)) {
			return value
		}
		
		return '' // Para qualquer outra coisa, retorna vazio
	}

	const [localValues, setLocalValues] = React.useState({
		top: getNumericValueForDisplay(values.top),
		right: getNumericValueForDisplay(values.right),
		bottom: getNumericValueForDisplay(values.bottom),
		left: getNumericValueForDisplay(values.left)
	})

	// Removendo o useEffect que estava causando o problema
	// O componente agora é controlado apenas pelo estado local

	const handleChange = (side: 'top' | 'right' | 'bottom' | 'left', value: string) => {
		// Atualiza o estado local
		setLocalValues((prev) => ({ ...prev, [side]: value }))
	}

	const handleBlur = (side: 'top' | 'right' | 'bottom' | 'left', value: string) => {
		const newValuesToParent = { ...values }
		const numericValue = parseInt(value)

		if (value === '' || isNaN(numericValue)) {
			// Se o campo está vazio ou não é um número válido, remove a propriedade
			delete newValuesToParent[side]
			// Mantém o estado local vazio
			setLocalValues((prev) => ({ ...prev, [side]: '' }))
		} else {
			// Se é um número válido (incluindo 0), formata com a unidade para o pai
			const formattedValue = String(numericValue)
			newValuesToParent[side] = `${formattedValue}${unit}`
			// Atualiza o estado local para mostrar apenas o número (sem unidade)
			setLocalValues((prev) => ({ ...prev, [side]: formattedValue }))
		}
		
		// Envia o objeto com propriedades removidas (undefined) para campos vazios
		onChange(newValuesToParent)
	}

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// Permitir apenas números e teclas de controle (Backspace, Delete, Tab, Enter, Arrows)
		const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
		if (!allowedKeys.includes(e.key)) {
			e.preventDefault()
		}
	}

	return (
		<div>
			<label className="block text-xs font-medium text-gray-700 mb-1">
				{label}
			</label>
			<div className="grid grid-cols-4 gap-2">
				<div>
					<label className="flex items-center justify-center text-xs text-gray-600 mb-1" title="Esquerda">
						<ArrowLeft className="w-3 h-3" />
					</label>
					<input
						key="spacing-left-input"
						type="text"
						value={localValues.left || ''}
						onChange={(e) => handleChange('left', e.target.value)}
						onBlur={(e) => handleBlur('left', e.target.value)}
						onKeyDown={handleKeyPress}
						placeholder={placeholder}
						className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
					/>
				</div>
				<div>
					<label className="flex items-center justify-center text-xs text-gray-600 mb-1" title="Superior">
						<ArrowUp className="w-3 h-3" />
					</label>
					<input
						key="spacing-top-input"
						type="text"
						value={localValues.top || ''} // Garante que é sempre uma string
						onChange={(e) => handleChange('top', e.target.value)}
						onBlur={(e) => handleBlur('top', e.target.value)}
						onKeyDown={handleKeyPress}
						placeholder={placeholder}
						className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
					/>
				</div>
				<div>
					<label className="flex items-center justify-center text-xs text-gray-600 mb-1" title="Direita">
						<ArrowRight className="w-3 h-3" />
					</label>
					<input
						key="spacing-right-input"
						type="text"
						value={localValues.right || ''}
						onChange={(e) => handleChange('right', e.target.value)}
						onBlur={(e) => handleBlur('right', e.target.value)}
						onKeyDown={handleKeyPress}
						placeholder={placeholder}
						className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
					/>
				</div>
				<div>
					<label className="flex items-center justify-center text-xs text-gray-600 mb-1" title="Inferior">
						<ArrowDown className="w-3 h-3" />
					</label>
					<input
						key="spacing-bottom-input"
						type="text"
						value={localValues.bottom || ''}
						onChange={(e) => handleChange('bottom', e.target.value)}
						onBlur={(e) => handleBlur('bottom', e.target.value)}
						onKeyDown={handleKeyPress}
						placeholder={placeholder}
						className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-center"
					/>
				</div>
			</div>
		</div>
	)
}
